import { Link } from "react-router-dom";
import { H3, H3Live, H3Spark, AssetDot } from "./h3";
import { fmtPrice, fmtChange, changeColor } from "../tokens";
import { useV2Markets, useV2Sectors, useV2Sparks } from "../useV2Data";

const ENTITY_SYM: Record<string, string> = {
  bitcoin: "BTC", ethereum: "ETH", solana: "SOL", ripple: "XRP", xrp: "XRP",
  cardano: "ADA", dogecoin: "DOGE",
};
const SECTOR_NAMES: Record<string, string> = {
  crypto: "Crypto", defi: "DeFi", web3: "Web3", blockchain: "Blockchain",
  "government-policy": "Policy", markets: "Markets", research: "Research",
};

/* Market Impact — Figma 214:187 composition, values driven ONLY by the
   Signal Lens methodology (HIGH-confidence edges; no relationship = no
   reaction). The Figma sample "STORY IMPACT SCORE 75" is NOT manufactured:
   the ring truthfully shows the count of HIGH-confidence linked signals, as
   authored in the 768/390 masters. Reduced state when no edges exist. */
export const MarketImpact = ({ hero, lens }: { hero: any; lens: any }) => {
  const { data: mkts } = useV2Markets();
  const { data: sectors } = useV2Sectors();
  const { data: sp } = useV2Sparks();
  const edges: any[] = ((lens?.edges || {})[hero?.id] || []).filter((e: any) => e.confidence === "HIGH");
  const coins: any[] = mkts?.coins || [];
  const priceOf = (sym: string) => coins.find((c) => c.symbol === sym);

  const symbols = [...new Set(edges.map((e) =>
    e.type === "market" ? e.target?.symbol
      : e.type === "entity" ? ENTITY_SYM[e.target?.slug || ""] : null).filter(Boolean))] as string[];
  const assetRows = symbols.map((s) => ({ sym: s, coin: priceOf(s) })).filter((r) => r.coin).slice(0, 4);

  const sectorEdge = edges.find((e) => e.type === "sector");
  const sectorSlug = sectorEdge?.target?.slug;
  const sectorCount = (sectors?.sectors || []).find((s: any) => s.slug === sectorSlug)?.count;
  const primary = edges.find((e) => e.type === "market") || edges.find((e) => e.type === "entity") || edges[0];
  const hasSignals = edges.length > 0;
  const rows = hasSignals
    ? assetRows
    : ["BTC", "ETH", "SOL"].map((s) => ({ sym: s, coin: priceOf(s) })).filter((r) => r.coin);

  const Row = ({ sym, coin }: { sym: string; coin: any }) => {
    const spark: number[] = sp?.sparks?.[sym] || [];
    const cc = changeColor(coin.change_24h);
    return (
      <div className="flex items-center gap-[10px] py-[5px] xl:py-[4px]" data-testid={`h3-impact-asset-${sym}`}>
        <AssetDot sym={sym} size={14} />
        <span className="nl-geist text-[11px] md:text-[12px] xl:text-[10px] min-[1440px]:text-[11px] leading-[16px] font-semibold w-[38px]" style={{ color: H3.text }}>{sym}</span>
        <span className="nl-geist text-[11px] md:text-[12px] xl:text-[10px] min-[1440px]:text-[11px] leading-[16px]" style={{ color: H3.text }}>{fmtPrice(coin.price)}</span>
        <span className="nl-mono text-[11px] md:text-[12px] xl:text-[10px] min-[1440px]:text-[11px] leading-[16px] ml-auto" style={{ color: cc }}>
          {fmtChange(coin.change_24h)}
        </span>
        {spark.length >= 2 && <H3Spark vals={spark} color={cc} w={48} h={10} sw={1.25} />}
      </div>
    );
  };

  const sectorLine = sectorSlug && (
    <div className="mt-[8px] xl:mt-0 xl:py-[8px]" data-testid="h3-impact-sector">
      <span className="nl-mono text-[10px] xl:text-[8px] min-[1440px]:text-[9px] leading-[14px]" style={{ color: H3.gray }}>RELATED SECTOR</span>
      <span className="nl-geist text-[12px] xl:text-[11px] leading-[16px] font-medium ml-[8px]" style={{ color: H3.text }}>
        {SECTOR_NAMES[sectorSlug] || sectorSlug}
      </span>
      {sectorCount != null && (
        <span className="nl-mono text-[11px] xl:text-[10px] leading-[15px] font-bold ml-[10px]" style={{ color: H3.green }}>
          {sectorCount} TODAY
        </span>
      )}
    </div>
  );

  return (
    <div data-testid="h3-market-impact" className="rounded-[10px] xl:rounded-[6px] p-[16px] flex flex-col"
      style={{ background: H3.card, border: `1px solid ${H3.border}` }}>
      <div className="flex items-center justify-between gap-[8px]">
        <p className="nl-geist text-[14px] md:text-[15px] xl:text-[13px] min-[1440px]:text-[15px] leading-[19px] font-bold" style={{ color: H3.cyan }}>
          MARKET IMPACT
        </p>
        <H3Live at={mkts?.updated_at} testid="h3-impact-live" />
      </div>

      <div className="md:grid md:grid-cols-2 md:gap-[20px] xl:block flex-1">
        <div>
          <p className="hidden xl:block nl-mono text-[10px] leading-[14px] mt-[14px]" style={{ color: H3.gray }}>
            LINKED SIGNALS
          </p>

          <div className="flex items-center gap-[16px] mt-[12px] xl:mt-[8px]">
            <div className="relative shrink-0 w-[82px] h-[82px] xl:w-[78px] xl:h-[78px] min-[1440px]:w-[88px] min-[1440px]:h-[88px]" role="img"
              aria-label={`${edges.length} high-confidence linked signals for this story`}>
              <svg viewBox="0 0 88 88" className="w-full h-full">
                <circle cx="44" cy="44" r="38" fill="none" stroke={hasSignals ? H3.violet : H3.slate} strokeWidth="8"
                  opacity={hasSignals ? 1 : 0.6} />
              </svg>
              <span className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="nl-sg text-[30px] leading-[32px] font-bold" style={{ color: H3.text }} data-testid="h3-impact-count">
                  {edges.length}
                </span>
                {hasSignals && (
                  <span className="nl-geist text-[9px] leading-[11px] font-bold" style={{ color: H3.violetText }}>HIGH</span>
                )}
              </span>
            </div>
            <div className="min-w-0">
              <p className="nl-geist text-[11px] md:text-[12px] xl:text-[11px] leading-[15px] font-semibold" style={{ color: H3.text }}>
                HIGH-CONFIDENCE<br />LINKED SIGNALS
              </p>
              {!hasSignals && (
                <p className="nl-geist text-[10px] leading-[14px] mt-[6px]" style={{ color: H3.gray }} data-testid="h3-impact-reduced">
                  No confirmed relationships for this story — no relationship, no reaction.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-[14px] md:mt-[6px] xl:mt-[14px]">
          <p className="nl-mono text-[9px] md:text-[10px] xl:text-[9px] leading-[13px]" style={{ color: H3.gray }}>
            {hasSignals ? "TOP ASSETS AFFECTED" : "MARKET STATE"}
          </p>
          <div className="mt-[5px]">
            {rows.map((r) => <Row key={r.sym} {...r} />)}
            {hasSignals && !rows.length && (
              <p className="nl-geist text-[11px] leading-[16px] mt-[4px]" style={{ color: H3.gray }}>
                No tracked assets linked to this story.
              </p>
            )}
          </div>
        </div>
      </div>

      {sectorSlug && <div className="xl:border-t mt-[6px] xl:mt-[8px]" style={{ borderColor: H3.border }}>{sectorLine}</div>}

      {primary && (
        <div className="border-t mt-[6px] pt-[8px]" data-testid="h3-impact-primary" style={{ borderColor: H3.border }}>
          <p className="nl-mono text-[8px] leading-[12px]" style={{ color: H3.gray }}>KEY CONTEXT</p>
          <p className="nl-geist text-[10px] xl:text-[9px] min-[1440px]:text-[10px] leading-[14px] mt-[3px] line-clamp-2" style={{ color: H3.meta }}>
            {(primary.target?.label || primary.target?.symbol || primary.target?.slug || "").toString()}
            {primary.why ? ` · ${primary.why}` : ""}
          </p>
        </div>
      )}

      <Link to={`/article/${hero.slug}`} data-testid="h3-impact-link"
        className="hidden xl:block nl-mono text-[8px] min-[1440px]:text-[9px] leading-[13px] mt-[10px] hover:opacity-80 transition-opacity"
        style={{ color: H3.violetText }}>
        VIEW FULL STORY →
      </Link>
    </div>
  );
};
